/**
 * 路径规划管理器
 *
 * 层级：controller（"业务怎么做"）
 *
 * 职责：
 * 1. 状态机 — enterMode/exitMode/选点/输入应用/开始规划/清除/重置
 * 2. 选点订阅 — pick 模式且 pickingStart/pickingEnd 时订阅 map:click，其余时刻解绑
 *    （先例：DrawingInteraction 也直接订阅 engine.events，仅会话激活时绑定）
 * 3. 输入校验 — input 模式 blur/Enter/开始规划前应用坐标，非法不覆盖旧点
 * 4. 渲染委托 — 起终点 marker / 路线统一走 ROUTE_PLAN_LAYER 独立图层
 *
 * 不知道 BMapGL 存在，只依赖 MapEngine 门面；不直接依赖 Zustand，经 IRoutePlanStoreBridge 读写
 */
import { LayerIdEnum } from '@/utils/constant';
import { HttpStatusCode } from '@/fetch/core/constant';
import type MapEngine from '@/map-core';
import type { ArrowLineHandle } from '@/map-core/lineLayer';
import type {
  GetWebRoutePlanParams,
  RoutePlanLngLat,
  RoutePlanResponse,
  WebRoutePlanData,
} from '../utils/fetch';
import {
  formatCoord,
  parseCoordinate,
  ROUTE_LINE_STYLE,
  ROUTE_PLAN_START_ICON,
  ROUTE_PLAN_END_ICON,
  createRouteLineTexture,
  ROUTE_LINE_TEXTURE_WIDTH,
  ROUTE_LINE_TEXTURE_HEIGHT,
} from '../utils/routePlan';
import type { IRoutePlanStoreBridge } from './storeBridge';
import {
  selectIsRoutePicking,
  type PointSourceMode,
  type RouteEndpoint,
} from '../store/routePlanStore';

const START_MARKER_ID = 'routePlanStart';
const END_MARKER_ID = 'routePlanEnd';

/** 起终点 marker 图标尺寸（与 SVG 图标一致） */
const MARKER_IMAGE_SIZE = { width: 24, height: 32 };
/** anchor [0.5, 1]：图钉底部尖端对准坐标点 */
const MARKER_ANCHOR: [number, number] = [0.5, 1];

const INPUT_ERROR_TEXT: Record<RouteEndpoint, string> = {
  start: '起点经纬度格式错误，应为 lng,lat',
  end: '终点经纬度格式错误，应为 lng,lat',
};

export class RoutePlanManager {
  /** map:click 解绑函数（events.on 返回值，成对管理，见 AGENTS.md 坑11） */
  private clickUnbind: (() => void) | null = null;

  /**
   * 在途请求代际令牌：每次 startPlan 自增，exitMode/resetPoints 失效旧请求。
   * await 返回后比对，防止"loading 中退出/重置后响应到达仍渲染路线、写 store"的竞态。
   */
  private requestSeq = 0;

  /** 最近一次成功选点（handleMapClick 消费）的时间戳，0 表示未消费过 */
  private lastPickConsumedAt = 0;

  /** 路线 LineLayer handle（不经 LayerRegistry，自持生命周期，所有清理路径都要 dispose） */
  private routeLineHandle: ArrowLineHandle | null = null;

  constructor(
    private getMap: () => MapEngine,
    private store: IRoutePlanStoreBridge,
    private fetchRoutePlan: (
      params: GetWebRoutePlanParams
    ) => Promise<RoutePlanResponse>
  ) {}

  /** 进入路径规划模式：清空旧状态，默认 pick 选点，进入 pickingStart */
  enterMode = () => {
    this.getMap().clearLayer(LayerIdEnum.ROUTE_PLAN_LAYER);
    this.store.enterPlanMode();
    this.syncClickSubscription();
  };

  /** 退出路径规划模式：解绑选点、仅清理 ROUTE_PLAN_LAYER（不动其他业务图层） */
  exitMode = () => {
    // 使在途规划请求失效，响应到达后不再渲染/写 store
    this.requestSeq++;
    this.unbindClick();
    this.disposeRouteLine();
    this.getMap().clearLayer(LayerIdEnum.ROUTE_PLAN_LAYER);
    this.store.exitPlanMode();
  };

  /** 地图点击选点（仅 pick 模式且 picking 阶段生效，由 syncClickSubscription 保证订阅时机） */
  handleMapClick = (payload: { lngLat?: [number, number] }) => {
    const snapshot = this.store.getSnapshot();
    if (!snapshot.planningMode || snapshot.pointSourceMode !== 'pick') return;
    const lngLat = payload?.lngLat;
    if (!lngLat) return;
    const point: RoutePlanLngLat = { lng: lngLat[0], lat: lngLat[1] };

    if (snapshot.routeStage === 'pickingStart') {
      this.applyPickedPoint('start', point);
      this.store.setRouteStage('pickingEnd');
    } else if (snapshot.routeStage === 'pickingEnd') {
      this.applyPickedPoint('end', point);
      this.store.setRouteStage('readyToPlan');
    } else {
      return;
    }
    // map:click 先于覆盖物 click 触发：记录选点消费时刻，
    // 供 coordinator.handleSelect 吞掉同一次点击带出的面详情弹窗
    this.lastPickConsumedAt = Date.now();
    this.syncClickSubscription();
  };

  /**
   * 选点消费时间窗：刚完成选点的 windowMs 内返回 true。
   * 用于处理 map:click 先于覆盖物 click 的时序——点终点落在面上时 stage 已推进到
   * readyToPlan，selectIsRoutePicking 谓词已 false，靠本时间窗吞掉随后带出的 handleSelect；
   * 窗口期外的正常点面看详情（spec 7.3）不受影响
   */
  wasPickJustConsumed = (windowMs = 500) =>
    this.lastPickConsumedAt > 0 &&
    Date.now() - this.lastPickConsumedAt < windowMs;

  /**
   * 切换起终点来源方式
   * pick→input：保留点并回填格式化坐标；input→pick：按点齐备度重算 stage
   */
  switchSourceMode = (mode: PointSourceMode) => {
    const snapshot = this.store.getSnapshot();
    // loading 中禁止切换，避免请求回来覆盖状态（PRD 19.4）
    if (snapshot.routeStage === 'loading') return;
    if (snapshot.pointSourceMode === mode) return;

    this.store.setPointSourceMode(mode);
    // 校验错误红字不跨模式残留：切换来源方式时清空双端 inputError
    this.store.setInputError('start', null);
    this.store.setInputError('end', null);
    if (mode === 'input') {
      if (snapshot.startPoint) {
        this.store.setStartInput(formatCoord(snapshot.startPoint));
      }
      if (snapshot.endPoint) {
        this.store.setEndInput(formatCoord(snapshot.endPoint));
      }
    }
    this.recomputeStage();
    this.syncClickSubscription();
  };

  /**
   * 应用输入坐标（blur / Enter / 开始规划前调用）
   * 合法：更新 marker + point + 格式化回填 + 清错误 + 重算 stage
   * 非法：只写错误文案，不覆盖旧点
   */
  applyInputPoint = (which: RouteEndpoint): boolean => {
    const snapshot = this.store.getSnapshot();
    const text = which === 'start' ? snapshot.startInput : snapshot.endInput;
    const result = parseCoordinate(text);
    if (!result.ok) {
      this.store.setInputError(which, INPUT_ERROR_TEXT[which]);
      return false;
    }
    this.store.setInputError(which, null);
    if (which === 'start') {
      this.store.setStartPoint(result.point);
      this.store.setStartInput(formatCoord(result.point));
      this.renderPointMarker(START_MARKER_ID, result.point, ROUTE_PLAN_START_ICON);
    } else {
      this.store.setEndPoint(result.point);
      this.store.setEndInput(formatCoord(result.point));
      this.renderPointMarker(END_MARKER_ID, result.point, ROUTE_PLAN_END_ICON);
    }
    this.recomputeStage();
    this.syncClickSubscription();
    return true;
  };

  /**
   * 开始规划：唯一触发接口请求的入口
   * input 模式先应用双端输入坐标，校验失败不请求；失败保留点，可重试
   */
  startPlan = async () => {
    let snapshot = this.store.getSnapshot();
    if (!snapshot.planningMode || snapshot.routeStage === 'loading') return;

    if (snapshot.pointSourceMode === 'input') {
      const startOk = this.applyInputPoint('start');
      const endOk = this.applyInputPoint('end');
      snapshot = this.store.getSnapshot();
      if (!startOk || !endOk || snapshot.startInputError || snapshot.endInputError) {
        return;
      }
    }
    if (!snapshot.startPoint || !snapshot.endPoint) return;

    const start = snapshot.startPoint;
    const end = snapshot.endPoint;
    // 发起新请求前清掉旧路线/里程，失败时不展示过期路线
    this.clearRouteLine();
    this.store.setPlanError(null);
    this.store.setRouteStage('loading');
    const seq = ++this.requestSeq;

    try {
      // 内部 {lng,lat} 映射为接口形态 {latitude,longitude}，alternatives 固定 0
      const res = await this.fetchRoutePlan({
        origin: { latitude: start.lat, longitude: start.lng },
        destination: { latitude: end.lat, longitude: end.lng },
        alternatives: 0,
      });
      // 在途期间已退出/重置：本次响应失效，不渲染不写 store
      if (seq !== this.requestSeq) return;
      const data = res?.data;
      if (
        res?.code === HttpStatusCode.Success &&
        data &&
        Array.isArray(data.path) &&
        data.path.length > 1
      ) {
        this.renderRouteLine(data.path);
        this.store.setRouteResult(data.distance, data.duration);
        this.store.setRouteStage('success');
      } else {
        this.store.setPlanError(res?.message || '路径规划失败，请稍后重试');
        this.store.setRouteStage('fail');
      }
    } catch {
      if (seq !== this.requestSeq) return;
      this.store.setPlanError('路径规划失败，请稍后重试');
      this.store.setRouteStage('fail');
    }
  };

  /** 清除路线 = 清线也清点（含输入框），回到待选点状态（stage 由 store 按来源方式复位） */
  clearRoute = () => {
    this.resetPoints();
  };

  /** 重新选点 / 清空坐标 = 清点也清线（stage 由 store 按来源方式复位） */
  resetPoints = () => {
    const snapshot = this.store.getSnapshot();
    if (snapshot.routeStage === 'loading') return;
    // 防御：使可能存在的在途请求失效
    this.requestSeq++;
    const layer = this.getMap().getLayer(LayerIdEnum.ROUTE_PLAN_LAYER);
    layer?.removeFeature(START_MARKER_ID);
    layer?.removeFeature(END_MARKER_ID);
    this.clearRouteLine();
    this.store.resetPoints();
    this.syncClickSubscription();
  };

  /** 销毁：解绑选点订阅 + 释放路线 LineLayer + 清图层（marker） */
  destroy = () => {
    this.unbindClick();
    this.disposeRouteLine();
    this.getMap().clearLayer(LayerIdEnum.ROUTE_PLAN_LAYER);
  };

  // ---- 内部实现 ----

  /** 选点落点：清旧路线/里程 + 写点 + 渲染 marker + 回填格式化输入框 */
  private applyPickedPoint = (which: RouteEndpoint, point: RoutePlanLngLat) => {
    this.clearRouteLine();
    if (which === 'start') {
      this.store.setStartPoint(point);
      this.store.setStartInput(formatCoord(point));
      this.store.setInputError('start', null);
      this.renderPointMarker(START_MARKER_ID, point, ROUTE_PLAN_START_ICON);
    } else {
      this.store.setEndPoint(point);
      this.store.setEndInput(formatCoord(point));
      this.store.setInputError('end', null);
      this.renderPointMarker(END_MARKER_ID, point, ROUTE_PLAN_END_ICON);
    }
  };

  /**
   * 按点齐备度重算 stage（loading 中不打断）：
   * 齐备 → readyToPlan；input 模式缺点 → idle；pick 模式 → pickingStart/pickingEnd
   */
  private recomputeStage = () => {
    const snapshot = this.store.getSnapshot();
    if (snapshot.routeStage === 'loading') return;
    if (snapshot.startPoint && snapshot.endPoint) {
      this.store.setRouteStage('readyToPlan');
    } else if (snapshot.pointSourceMode === 'input') {
      this.store.setRouteStage('idle');
    } else {
      this.store.setRouteStage(
        snapshot.startPoint ? 'pickingEnd' : 'pickingStart'
      );
    }
  };

  /** click 订阅管理：仅 pick 模式且 picking 阶段绑定（共享谓词），其余时刻解绑 */
  private syncClickSubscription = () => {
    const shouldBind = selectIsRoutePicking(this.store.getSnapshot());
    if (shouldBind && !this.clickUnbind) {
      this.clickUnbind = this.getMap().events.on(
        'map:click',
        this.handleMapClick
      );
    } else if (!shouldBind) {
      this.unbindClick();
    }
  };

  private unbindClick = () => {
    this.clickUnbind?.();
    this.clickUnbind = null;
  };

  /** 获取或创建路径规划独立图层（先例：LaneManager drawLines） */
  private ensureRouteLayer = () => {
    const map = this.getMap();
    return (
      map.getLayer(LayerIdEnum.ROUTE_PLAN_LAYER) ??
      map.createLayer(LayerIdEnum.ROUTE_PLAN_LAYER)
    );
  };

  /** 渲染/移动起终点 marker：先移除旧 feature 再重建 */
  private renderPointMarker = (
    id: string,
    point: RoutePlanLngLat,
    icon: string
  ) => {
    const layer = this.ensureRouteLayer();
    layer.removeFeature(id);
    const feature = this.getMap().createPointFeature(
      [point.lng, point.lat],
      icon,
      id,
      { imageSize: MARKER_IMAGE_SIZE },
      MARKER_ANCHOR,
      LayerIdEnum.ROUTE_PLAN_LAYER
    );
    layer.getSource().addFeature(feature);
  };

  /** 渲染路线（后端 path 为 {longitude,latitude} 点串，GCJ02）。LineLayer 不经 LayerRegistry，handle 自持 */
  private renderRouteLine = (path: WebRoutePlanData['path']) => {
    const coordinates: [number, number][] = path.map((point) => [
      point.longitude,
      point.latitude,
    ]);
    if (this.routeLineHandle) {
      this.routeLineHandle.setData(coordinates);
      return;
    }
    this.routeLineHandle = this.getMap().createArrowLine(coordinates, {
      color: ROUTE_LINE_STYLE.color,
      weight: ROUTE_LINE_STYLE.width,
      textureUrl: createRouteLineTexture(),
      textureWidth: ROUTE_LINE_TEXTURE_WIDTH,
      textureHeight: ROUTE_LINE_TEXTURE_HEIGHT,
    });
  };

  /** 释放路线 LineLayer（marker 走 ROUTE_PLAN_LAYER 的 clearLayer，路线独立 dispose） */
  private disposeRouteLine = () => {
    this.routeLineHandle?.dispose();
    this.routeLineHandle = null;
  };

  /** 仅清路线 + 里程结果，保留起终点 */
  private clearRouteLine = () => {
    this.disposeRouteLine();
    this.store.clearRouteResult();
  };
}
